import { FEATURES } from "@/lib/features";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr, BACKEND_URL } from "@/lib/api";
import { IMG } from "@/lib/assets";
import { PageBanner } from "@/components/ui/PageBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, CameraOff, Volume2, VolumeX, Dumbbell, Waves, Radio, Trophy, Timer, Play, Square } from "lucide-react";

const MODES = [
  { key: "gym", label: "Gym", icon: Dumbbell, img: IMG.strength },
  ...(FEATURES.padel ? [{ key: "padel", label: "Padel", icon: Trophy, img: IMG.padelHero }] : []),
  { key: "yoga", label: "Yoga", icon: Waves, img: IMG.yoga },
];

function RepTempo() {
  const [target, setTarget] = useState(10);
  const [tempo, setTempo] = useState(3);
  const [count, setCount] = useState(0);
  const [running, setRunning] = useState(false);
  const iv = useRef(null);

  const say = (t) => { if ("speechSynthesis" in window) { const u = new SpeechSynthesisUtterance(String(t)); u.rate = 1.05; window.speechSynthesis.speak(u); } };

  const start = () => {
    setCount(0); setRunning(true);
    let n = 0;
    say("Let's go");
    iv.current = setInterval(() => {
      n += 1; setCount(n); say(n);
      if (n >= Number(target)) { clearInterval(iv.current); setRunning(false); say("Set complete. Great work."); }
    }, Number(tempo) * 1000);
  };
  const stop = () => { clearInterval(iv.current); setRunning(false); };
  useEffect(() => () => clearInterval(iv.current), []);

  const pct = Math.min(100, (count / Number(target || 1)) * 100);
  return (
    <div className="surface rounded-lg p-5" data-testid="rep-tempo">
      <p className="mono-label mb-3 flex items-center gap-2"><Timer className="w-3.5 h-3.5 text-acid" /> Rep & Tempo counter</p>
      <div className="flex items-center justify-center my-2">
        <div className="relative w-28 h-28">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="44" fill="none" stroke="#1E222A" strokeWidth="8" />
            <circle cx="50" cy="50" r="44" fill="none" stroke="#CCFF00" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 44} strokeDashoffset={2 * Math.PI * 44 * (1 - pct / 100)}
              style={{ transition: "stroke-dashoffset 0.4s" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-heading font-black text-3xl" data-testid="rep-count">{count}</span>
            <span className="mono-label">/ {target} reps</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <div><label className="mono-label">Target reps</label><Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1 h-9 bg-[#0F1115] border-[#1E222A]" data-testid="rep-target" /></div>
        <div><label className="mono-label">Tempo (s/rep)</label><Input type="number" value={tempo} onChange={(e) => setTempo(e.target.value)} className="mt-1 h-9 bg-[#0F1115] border-[#1E222A]" data-testid="rep-tempo-sec" /></div>
      </div>
      {!running ? (
        <Button onClick={start} className="w-full mt-3 bg-acid text-black hover:bg-[#AACC00] rounded-md h-10" data-testid="rep-start"><Play className="w-4 h-4" /> Count me down</Button>
      ) : (
        <Button onClick={stop} className="w-full mt-3 bg-pulseError text-white hover:bg-[#e0342a] rounded-md h-10" data-testid="rep-stop"><Square className="w-4 h-4" /> Stop</Button>
      )}
      <p className="text-xs text-[#565C6F] mt-2">OCTON calls each rep at your tempo so you keep perfect cadence under load.</p>
    </div>
  );
}

export default function Train() {
  const [params] = useSearchParams();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const [mode, setMode] = useState(MODES.some((m) => m.key === params.get("mode")) ? params.get("mode") : "gym");
  const [active, setActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [speak, setSpeak] = useState(true);
  const [cues, setCues] = useState([]);

  useEffect(() => () => stop(), []); // cleanup on unmount

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 } }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setActive(true);
      setCues([]);
      toast.success("OCTON is watching. Move into frame.");
      captureLoop();
    } catch (err) {
      toast.error("Camera access denied. Allow the camera to train with OCTON.");
    }
  };

  const stop = () => {
    clearTimeout(timerRef.current);
    audioRef.current?.pause();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setActive(false);
    setAnalyzing(false);
  };

  const captureLoop = async () => {
    await analyzeFrame();
    timerRef.current = setTimeout(captureLoop, 7000);
  };

  const analyzeFrame = async () => {
    const video = videoRef.current, canvas = canvasRef.current;
    if (!video || !canvas || video.videoWidth === 0) return;
    setAnalyzing(true);
    try {
      canvas.width = 640;
      canvas.height = (640 * video.videoHeight) / video.videoWidth;
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      const image = canvas.toDataURL("image/jpeg", 0.6);
      const { data } = await api.post("/api/coach/vision", { image, mode });
      const cue = data.cue;
      setCues((c) => [{ id: Date.now(), text: cue }, ...c].slice(0, 8));
      if (speak) speakCue(cue);
    } catch (err) {
      // vision frame skipped — keep the capture loop alive
    } finally { setAnalyzing(false); }
  };

  const speakCue = async (text) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ava/narrate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error();
      audioRef.current?.pause();
      audioRef.current = new Audio(URL.createObjectURL(await res.blob()));
      audioRef.current.play();
    } catch (_) {
      if ("speechSynthesis" in window) window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
    }
  };

  const modeMeta = MODES.find((m) => m.key === mode);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={modeMeta.img} overline="body · mind · soul" title="Train with OCTON"
        subtitle="Bring OCTON to the gym, the mat or the studio. It watches through your camera and coaches your form live, on any device." />

      {/* Mode selector */}
      <div className="flex gap-2 flex-wrap">
        {MODES.map((m) => (
          <button key={m.key} onClick={() => setMode(m.key)} data-testid={`train-mode-${m.key}`}
            className={`px-4 h-10 rounded-full text-sm border transition-colors flex items-center gap-2 ${
              mode === m.key ? "border-acid text-acid bg-acid/10" : "border-[#1E222A] text-[#8A91A6] hover:border-[#343A4A]"}`}>
            <m.icon className="w-4 h-4" /> {m.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Camera stage */}
        <div className="lg:col-span-8">
          <div className="relative rounded-xl overflow-hidden surface aspect-video">
            <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover"
              style={{ transform: "scaleX(-1)", display: active ? "block" : "none" }} data-testid="train-video" />
            <canvas ref={canvasRef} className="hidden" />
            {!active && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                <img src={modeMeta.img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                <div className="relative">
                  <Camera className="w-10 h-10 text-acid mx-auto mb-3" />
                  <p className="font-heading font-bold text-xl">Camera off</p>
                  <p className="text-[#8A91A6] text-sm mt-1 max-w-xs">Start a session and OCTON will watch your {modeMeta.label.toLowerCase()} form and coach you in real time.</p>
                </div>
              </div>
            )}
            {active && (
              <div className="absolute top-3 left-3 flex items-center gap-2 glass rounded-full px-3 py-1.5">
                <span className="w-2 h-2 rounded-full bg-pulseError animate-pulse" />
                <span className="mono-label text-white">REC · OCTON WATCHING</span>
              </div>
            )}
            {active && analyzing && (
              <div className="absolute top-3 right-3 flex items-center gap-2 glass rounded-full px-3 py-1.5">
                <Radio className="w-3.5 h-3.5 text-acid animate-pulse" />
                <span className="mono-label text-acid">analysing frame</span>
              </div>
            )}
            {/* latest cue overlay */}
            <AnimatePresence>
              {active && cues[0] && (
                <motion.div key={cues[0].id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="absolute bottom-4 left-4 right-4 glass rounded-lg p-4 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-acid flex items-center justify-center shrink-0">
                    <span className="font-heading font-black text-black">A</span>
                  </div>
                  <p className="text-white text-sm md:text-base leading-snug" data-testid="live-cue">{cues[0].text}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex items-center gap-3 mt-4">
            {!active ? (
              <Button onClick={start} className="bg-acid text-black hover:bg-[#AACC00] rounded-full h-11 px-6 font-medium" data-testid="start-train-btn">
                <Camera className="w-4 h-4" /> Start session
              </Button>
            ) : (
              <Button onClick={stop} className="bg-pulseError text-white hover:bg-[#e0342a] rounded-full h-11 px-6 font-medium" data-testid="stop-train-btn">
                <CameraOff className="w-4 h-4" /> End session
              </Button>
            )}
            <button onClick={() => setSpeak((s) => !s)} data-testid="speak-toggle"
              className={`h-11 px-4 rounded-full border text-sm flex items-center gap-2 transition-colors ${
                speak ? "border-acid text-acid" : "border-[#1E222A] text-[#8A91A6]"}`}>
              {speak ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} OCTON voice {speak ? "on" : "off"}
            </button>
          </div>
        </div>

        {/* Rep counter + coaching feed */}
        <div className="lg:col-span-4 space-y-4">
          <RepTempo />
          <div className="surface rounded-lg p-5">
            <p className="mono-label mb-3">Live coaching feed</p>
            <div className="space-y-2 max-h-[46vh] overflow-y-auto">
              {cues.length === 0 && <p className="text-[#565C6F] text-sm">OCTON's cues will stream here as you train.</p>}
              {cues.map((c, i) => (
                <motion.div key={c.id} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                  className="surface-elev rounded-md p-3 text-sm text-white/90" data-testid={`cue-${i}`}>
                  {c.text}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
